import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Phone, Calendar, LayoutDashboard, Search, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { BRAND } from '../../data/mock';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDashboardOpen, setIsDashboardOpen] = useState(false);
  const [bookingInput, setBookingInput] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  const handleDashboardSubmit = (e) => {
    e.preventDefault();
    const input = bookingInput.trim();
    if (!input) return;

    // Support pasting the full URL or just the booking ID
    const urlMatch = input.match(/\/dashboard\/(APT[A-Z0-9]+)/);
    const tokenMatch = input.match(/[?&]token=([^&\s]+)/);

    const bookingId = urlMatch ? urlMatch[1] : input.toUpperCase();
    const token = tokenMatch ? tokenMatch[1] : null;

    setIsDashboardOpen(false);
    setIsMenuOpen(false);
    setBookingInput('');

    const path = `/dashboard/${bookingId}${token ? `?token=${token}` : ''}`;
    navigate(path);
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#FDF9F5]/95 backdrop-blur-md shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center group">
              <img
                src={BRAND.logo}
                alt={BRAND.name}
                className="h-16 w-auto object-contain"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isActive(link.path)
                      ? 'bg-[#2F5233]/10 text-[#2F5233]'
                      : 'text-gray-600 hover:text-[#E91E8C] hover:bg-[#E91E8C]/5'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <button
                onClick={() => setIsDashboardOpen(true)}
                className="px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 text-gray-600 hover:text-[#E91E8C] hover:bg-[#E91E8C]/5 flex items-center space-x-1"
              >
                <LayoutDashboard className="w-4 h-4" />
                <span>My Appointments</span>
              </button>
            </nav>

            {/* CTA Buttons */}
            <div className="hidden md:flex items-center space-x-3">
              <a
                href="tel:+917353196539"
                className="flex items-center space-x-2 text-gray-600 hover:text-[#E91E8C] transition-colors"
              >
                <Phone className="w-4 h-4" />
                <span className="text-sm font-medium">Call Us</span>
              </a>
              <Link to="/book">
                <Button className="bg-[#2F5233] hover:bg-[#2F5233]/90 text-white shadow-lg hover:shadow-xl transition-all duration-300">
                  <Calendar className="w-4 h-4 mr-2" />
                  Book Appointment
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              {isMenuOpen ? (
                <X className="w-6 h-6 text-gray-600" />
              ) : (
                <Menu className="w-6 h-6 text-gray-600" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          className={`md:hidden absolute top-20 left-0 right-0 bg-[#FDF9F5] shadow-lg transition-all duration-300 ${
            isMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none'
          }`}
        >
          <nav className="px-4 py-4 space-y-2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMenuOpen(false)}
                className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                  isActive(link.path)
                    ? 'bg-[#2F5233]/10 text-[#2F5233]'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                {link.name}
              </Link>
            ))}
            <button
              onClick={() => { setIsMenuOpen(false); setIsDashboardOpen(true); }}
              className="w-full text-left block px-4 py-3 rounded-lg text-sm font-medium transition-colors text-gray-600 hover:bg-gray-50 flex items-center space-x-2"
            >
              <LayoutDashboard className="w-4 h-4" />
              <span>My Appointments</span>
            </button>
            <div className="pt-4 border-t">
              <Link to="/book" onClick={() => setIsMenuOpen(false)}>
                <Button className="w-full bg-[#2F5233] hover:bg-[#2F5233]/90 text-white">
                  <Calendar className="w-4 h-4 mr-2" />
                  Book Appointment
                </Button>
              </Link>
            </div>
          </nav>
        </div>
      </header>

      {/* Dashboard Modal */}
      {isDashboardOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setIsDashboardOpen(false)}
          />
          {/* Modal */}
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-8">
            <button
              onClick={() => setIsDashboardOpen(false)}
              className="absolute top-4 right-4 p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>

            <div className="text-center mb-6">
              <div className="w-14 h-14 rounded-2xl bg-[#2F5233]/10 flex items-center justify-center mx-auto mb-4">
                <LayoutDashboard className="w-7 h-7 text-[#2F5233]" />
              </div>
              <h2 className="text-xl font-serif font-bold text-[#2F5233]">Access Your Dashboard</h2>
              <p className="text-gray-500 text-sm mt-2">
                Enter your Booking ID or paste the full dashboard link from your email.
              </p>
            </div>

            <form onSubmit={handleDashboardSubmit} className="space-y-4">
              <div>
                <Input
                  value={bookingInput}
                  onChange={(e) => setBookingInput(e.target.value)}
                  placeholder="e.g. APTFD85AA or full dashboard URL"
                  className="h-12 text-sm border-[#2F5233]/30 focus:border-[#2F5233]"
                  autoFocus
                />
                <p className="text-xs text-gray-400 mt-2">
                  Your Booking ID and dashboard link were sent to your email after booking.
                </p>
              </div>
              <Button
                type="submit"
                className="w-full bg-[#2F5233] hover:bg-[#2F5233]/90 text-white h-12"
                disabled={!bookingInput.trim()}
              >
                <Search className="w-4 h-4 mr-2" />
                Open Dashboard
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
